import React, { useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useOrg } from "@/context/OrgContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Buildings, Plus, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Organizations() {
  const { t } = useLang();
  const { orgs, activeOrgId, switchOrg, refresh } = useOrg();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", reporting_year: new Date().getFullYear(), country: "Global" });

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/organizations", { ...form, reporting_year: parseInt(form.reporting_year) });
      toast.success(t("orgCreated"));
      setShowForm(false);
      setForm({ name: "", reporting_year: new Date().getFullYear(), country: "Global" });
      await refresh();
      switchOrg(data.id);
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const doSwitch = (id) => {
    switchOrg(id);
    window.location.reload();
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">Multi-org workspace</div>
          <h1 className="font-display text-3xl font-semibold mt-1">{t("organizations")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{orgs.length} {t("orgs")}</p>
        </div>
        <Button onClick={() => setShowForm((s) => !s)} data-testid="org-new-toggle">
          <Plus size={16} className="mr-1" /> {showForm ? t("cancel") : t("newOrg")}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader><CardTitle className="font-display text-lg">{t("createOrg")}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="org-new-form">
              <div className="md:col-span-2 space-y-2">
                <Label>{t("orgName")}</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="org-new-name" />
              </div>
              <div className="space-y-2">
                <Label>{t("reportingYear")}</Label>
                <Input type="number" required value={form.reporting_year} onChange={(e) => setForm({ ...form, reporting_year: e.target.value })} data-testid="org-new-year" />
              </div>
              <div className="space-y-2">
                <Label>{t("country")}</Label>
                <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="org-new-country" />
              </div>
              <div className="md:col-span-3">
                <Button type="submit" data-testid="org-new-submit">{t("createOrg")}</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {orgs.map((o) => (
          <Card
            key={o.id}
            className={`border ${o.id === activeOrgId ? "border-primary/50 bg-primary/5" : ""}`}
            data-testid={`org-card-${o.id}`}
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Buildings size={20} weight="duotone" className="text-primary" />
                  <div>
                    <div className="font-display text-lg font-semibold">{o.name}</div>
                    <div className="text-xs font-mono-data text-muted-foreground uppercase tracking-widest">
                      {o.role} · {o.reporting_year} · {o.country || "Global"}
                    </div>
                  </div>
                </div>
                {o.id === activeOrgId && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono-data uppercase text-primary bg-primary/10 px-2 py-1 rounded-sm" data-testid="active-org-badge">
                    <CheckCircle size={12} weight="fill" /> {t("activeOrg")}
                  </span>
                )}
              </div>
              {o.id !== activeOrgId && (
                <Button variant="outline" size="sm" onClick={() => doSwitch(o.id)} data-testid={`org-switch-${o.id}`}>
                  {t("switchOrg")}
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
